import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Row, Col} from 'react-bootstrap'
import {UserModel} from '../../auth/core/_models'

type Props = {
  submissionError: string | null
  creating: boolean
  onSubmit: (values: FuturiskFreeHealthCheckupFormValues) => Promise<void>
  currentUser?: UserModel
}

export interface FuturiskFreeHealthCheckupFormValues {
  Name: string
  age: string
  gender: string
  phoneNumber: string
  emailId: string
  officialId: string
  completeAddress: string
  pincode: string
  homeCollectionDate: string
  homeCollectionTime: string
}

const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getMinCollectionDate = () => {
  const nextDay = new Date()
  nextDay.setDate(nextDay.getDate() + 1)
  nextDay.setHours(0, 0, 0, 0)
  return formatDateInputValue(nextDay)
}

const getMaxCollectionDate = () => {
  const maxDate = new Date()
  maxDate.setDate(maxDate.getDate() + 30)
  maxDate.setHours(0, 0, 0, 0)
  return formatDateInputValue(maxDate)
}

const formatCollectionDate = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  return date.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

const calculateAgeFromDob = (dob?: string | Date) => {
  if (!dob) {
    return ''
  }

  const parsedDob = new Date(dob)
  if (Number.isNaN(parsedDob.getTime())) {
    return ''
  }

  const today = new Date()
  let age = today.getFullYear() - parsedDob.getFullYear()
  const monthDifference = today.getMonth() - parsedDob.getMonth()

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < parsedDob.getDate())) {
    age -= 1
  }

  return age > 0 ? String(age) : ''
}

const normalizeGender = (gender?: string) => {
  if (!gender) {
    return ''
  }

  const normalizedGender = gender.trim().toLowerCase()

  if (normalizedGender === 'm' || normalizedGender === 'male') {
    return 'Male'
  }

  if (normalizedGender === 'f' || normalizedGender === 'female') {
    return 'Female'
  }

  if (normalizedGender === 'o' || normalizedGender === 'other' || normalizedGender === 'others') {
    return 'Other'
  }

  return gender
}

const formatSlotBoundary = (hour: number) => {
  const normalizedHour = hour % 12 || 12
  const meridiem = hour >= 12 ? 'PM' : 'AM'
  return `${String(normalizedHour).padStart(2, '0')}:00 ${meridiem}`
}

const buildTimeSlots = () =>
  Array.from({length: 13}, (_, index) => {
    const startHour = 6 + index
    const endHour = startHour + 1
    return `${formatSlotBoundary(startHour)} - ${formatSlotBoundary(endHour)}`
  })

const validationSchema = Yup.object().shape({
  Name: Yup.string().trim().required('Name is required'),
  age: Yup.string()
    .matches(/^\d{1,3}$/, 'Age must be a valid number')
    .test('age-range', 'Age must be between 1 and 120', (value) => {
      if (!value) {
        return false
      }

      const age = Number(value)
      return age >= 1 && age <= 120
    })
    .required('Age is required'),
  gender: Yup.string().required('Gender is required'),
  phoneNumber: Yup.string()
    .matches(/^\d{10}$/, 'Contact number must be exactly 10 digits')
    .required('Contact number is required'),
  emailId: Yup.string().email('Invalid email ID').required('Email ID is required'),
  officialId: Yup.string().email('Invalid official email ID').required('Official email ID is required'),
  completeAddress: Yup.string().trim().required('Complete address is required'),
  pincode: Yup.string()
    .matches(/^\d{6}$/, 'Pincode must be exactly 6 digits')
    .required('Pincode is required'),
  homeCollectionDate: Yup.string()
    .required('Collection date is required')
    .test('collection-date-range', 'Select a date from tomorrow to the next 30 days', (value) => {
      if (!value) {
        return false
      }

      const selectedDate = new Date(value)
      if (Number.isNaN(selectedDate.getTime())) {
        return false
      }

      const minDate = new Date(getMinCollectionDate())
      const maxDate = new Date(getMaxCollectionDate())

      selectedDate.setHours(0, 0, 0, 0)
      minDate.setHours(0, 0, 0, 0)
      maxDate.setHours(0, 0, 0, 0)

      return selectedDate >= minDate && selectedDate <= maxDate
    }),
  homeCollectionTime: Yup.string().required('Home collection time is required'),
})

const FuturiskFreeHealthCheckupForm: React.FC<Props> = ({
  submissionError,
  creating,
  onSubmit,
  currentUser,
}) => {
  const minCollectionDate = getMinCollectionDate()
  const maxCollectionDate = getMaxCollectionDate()
  const timeSlots = buildTimeSlots()

  const formik = useFormik<FuturiskFreeHealthCheckupFormValues>({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      Name: currentUser?.Name || '',
      age: calculateAgeFromDob(currentUser?.Dob),
      gender: normalizeGender(currentUser?.Gender),
      phoneNumber: currentUser?.PhoneNumber || '',
      emailId: currentUser?.Email || '',
      officialId: currentUser?.Email || currentUser?.EmployeeId || currentUser?.UserName || '',
      completeAddress: '',
      pincode: '',
      homeCollectionDate: minCollectionDate,
      homeCollectionTime: '',
    },
    validationSchema,
    onSubmit: async (values, {setSubmitting}) => {
      await onSubmit(values)
      setSubmitting(false)
    },
  })

  const homeCollectionDateLabel = formatCollectionDate(formik.values.homeCollectionDate || minCollectionDate)

  return (
    <div className='position-relative'>
      <form onSubmit={formik.handleSubmit}>
        {submissionError && (
          <div className='alert alert-danger' role='alert'>
            {submissionError}
          </div>
        )}

        <div className='alert alert-primary mb-7'>
          <div className='fw-bold mb-1'>Selected Home Collection Date</div>
          <div className='fs-6'>{homeCollectionDateLabel}</div>
          <div className='text-muted small mt-2'>Same-day booking is not available. You can book any date from tomorrow up to the next 30 days.</div>
        </div>

        <Row className='mb-7'>
          <Col md={6}>
            <label htmlFor='Name' className='form-label fw-bolder text-dark fs-6'>
              Name <span className='text-danger'>*</span>
            </label>
            <input
              id='Name'
              type='text'
              {...formik.getFieldProps('Name')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.Name && formik.errors.Name,
                'is-valid': formik.touched.Name && !formik.errors.Name,
              })}
            />
            {formik.touched.Name && formik.errors.Name && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.Name}</span>
                </div>
              </div>
            )}
          </Col>
          <Col md={3}>
            <label htmlFor='age' className='form-label fw-bolder text-dark fs-6'>
              Age <span className='text-danger'>*</span>
            </label>
            <input
              id='age'
              type='text'
              maxLength={3}
              {...formik.getFieldProps('age')}
              onInput={(event) => {
                const input = event.currentTarget
                input.value = input.value.replace(/\D/g, '').slice(0, 3)
                formik.setFieldValue('age', input.value)
              }}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.age && formik.errors.age,
                'is-valid': formik.touched.age && !formik.errors.age,
              })}
            />
            {formik.touched.age && formik.errors.age && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.age}</span>
                </div>
              </div>
            )}
          </Col>
          <Col md={3}>
            <label htmlFor='gender' className='form-label fw-bolder text-dark fs-6'>
              Gender <span className='text-danger'>*</span>
            </label>
            <select
              id='gender'
              {...formik.getFieldProps('gender')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.gender && formik.errors.gender,
                'is-valid': formik.touched.gender && !formik.errors.gender,
              })}
            >
              <option value=''>Select Gender</option>
              <option value='Male'>Male</option>
              <option value='Female'>Female</option>
              <option value='Other'>Other</option>
            </select>
            {formik.touched.gender && formik.errors.gender && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.gender}</span>
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Row className='mb-7'>
          <Col md={6}>
            <label htmlFor='phoneNumber' className='form-label fw-bolder text-dark fs-6'>
              Contact No. <span className='text-danger'>*</span>
            </label>
            <input
              id='phoneNumber'
              type='text'
              maxLength={10}
              {...formik.getFieldProps('phoneNumber')}
              onInput={(event) => {
                const input = event.currentTarget
                input.value = input.value.replace(/\D/g, '').slice(0, 10)
                formik.setFieldValue('phoneNumber', input.value)
              }}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.phoneNumber && formik.errors.phoneNumber,
                'is-valid': formik.touched.phoneNumber && !formik.errors.phoneNumber,
              })}
            />
            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.phoneNumber}</span>
                </div>
              </div>
            )}
          </Col>
          <Col md={6}>
            <label htmlFor='emailId' className='form-label fw-bolder text-dark fs-6'>
              Email ID <span className='text-danger'>*</span>
            </label>
            <input
              id='emailId'
              type='email'
              {...formik.getFieldProps('emailId')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.emailId && formik.errors.emailId,
                'is-valid': formik.touched.emailId && !formik.errors.emailId,
              })}
            />
            {formik.touched.emailId && formik.errors.emailId && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.emailId}</span>
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Row className='mb-7'>
          <Col md={12}>
            <label htmlFor='officialId' className='form-label fw-bolder text-dark fs-6'>
              Official Email ID for Tracking <span className='text-danger'>*</span>
            </label>
            <input
              id='officialId'
              type='email'
              {...formik.getFieldProps('officialId')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.officialId && formik.errors.officialId,
                'is-valid': formik.touched.officialId && !formik.errors.officialId,
              })}
            />
            <small className='text-muted d-block mt-2'>Prefilled from your email ID. Update it if your official email is different.</small>
            {formik.touched.officialId && formik.errors.officialId && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.officialId}</span>
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Row className='mb-7'>
          <Col md={12}>
            <label htmlFor='completeAddress' className='form-label fw-bolder text-dark fs-6'>
              Complete Address <span className='text-danger'>*</span>
            </label>
            <textarea
              id='completeAddress'
              rows={4}
              {...formik.getFieldProps('completeAddress')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.completeAddress && formik.errors.completeAddress,
                'is-valid': formik.touched.completeAddress && !formik.errors.completeAddress,
              })}
            />
            {formik.touched.completeAddress && formik.errors.completeAddress && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.completeAddress}</span>
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Row className='mb-7'>
          <Col md={12}>
            <label htmlFor='pincode' className='form-label fw-bolder text-dark fs-6'>
              Pincode <span className='text-danger'>*</span>
            </label>
            <input
              id='pincode'
              type='text'
              maxLength={6}
              {...formik.getFieldProps('pincode')}
              onInput={(event) => {
                const input = event.currentTarget
                input.value = input.value.replace(/\D/g, '').slice(0, 6)
                formik.setFieldValue('pincode', input.value)
              }}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.pincode && formik.errors.pincode,
                'is-valid': formik.touched.pincode && !formik.errors.pincode,
              })}
            />
            {formik.touched.pincode && formik.errors.pincode && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.pincode}</span>
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Row className='mb-7'>
          <Col md={6}>
            <label htmlFor='homeCollectionDate' className='form-label fw-bolder text-dark fs-6'>
              Home Collection Date <span className='text-danger'>*</span>
            </label>
            <input
              id='homeCollectionDate'
              type='date'
              min={minCollectionDate}
              max={maxCollectionDate}
              {...formik.getFieldProps('homeCollectionDate')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.homeCollectionDate && formik.errors.homeCollectionDate,
                'is-valid': formik.touched.homeCollectionDate && !formik.errors.homeCollectionDate,
              })}
            />
            {formik.touched.homeCollectionDate && formik.errors.homeCollectionDate && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.homeCollectionDate}</span>
                </div>
              </div>
            )}
            <small className='text-muted d-block mt-2'>Choose any date from tomorrow to the next 30 days.</small>
          </Col>
          <Col md={6}>
            <label htmlFor='homeCollectionTime' className='form-label fw-bolder text-dark fs-6'>
              Home Collection Time <span className='text-danger'>*</span>
            </label>
            <select
              id='homeCollectionTime'
              {...formik.getFieldProps('homeCollectionTime')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.homeCollectionTime && formik.errors.homeCollectionTime,
                'is-valid': formik.touched.homeCollectionTime && !formik.errors.homeCollectionTime,
              })}
            >
              <option value=''>Select Home Collection Time</option>
              {timeSlots.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))}
            </select>
            {formik.touched.homeCollectionTime && formik.errors.homeCollectionTime && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.homeCollectionTime}</span>
                </div>
              </div>
            )}
            <small className='text-muted d-block mt-2'>Slots are available from 6:00 AM to 7:00 PM for dates within the next 30 days.</small>
          </Col>
        </Row>

        <div className='text-center'>
          <button
            type='submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={!formik.isValid || formik.isSubmitting || creating}
          >
            Book Free Health Checkup
          </button>
        </div>
      </form>

      {creating && (
        <div
          className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
          style={{background: 'rgba(255,255,255,0.8)', zIndex: 10}}
        >
          <div className='text-center'>
            <div className='spinner-border text-primary' role='status'></div>
            <div className='mt-2'>Submitting booking...</div>
          </div>
        </div>
      )}
    </div>
  )
}

export {FuturiskFreeHealthCheckupForm}